import { addActionHandler, getGlobal } from '../global';

import type { ApiMessage } from '../api/types';
import type { ActionReturnType } from '../global/types';
import type { AutoReplyConfig, AutoReplyPart } from './seatBridge';

import { SERVICE_NOTIFICATIONS_USER_ID } from '../config';
import { isUserBot } from '../global/helpers/users';
import { selectChat, selectChatLastMessageId, selectUser } from '../global/selectors';
import { isUserId } from '../util/entities/ids';
import { getServerTime } from '../util/serverTime';
import { callApi } from '../api/gramjs';
import {
  claimSeatAutoReply, getSeatAutoReplyConfig, releaseSeatAutoReply, reportSeatAutoReply,
} from './seatBridge';

/**
 * 席位自动回复：别人私聊过来时，由这个席位账号自己回一句
 *
 * 全部在浏览器里跑（收到 Telegram 更新才触发），服务端只存配置与日志：
 * 收到私聊 → 按联系人/陌生人开关过滤 → 挂一个延时接管的定时器 → 期间人工回复就取消 →
 * 到点按关键词选话术、按档位插混淆字符发出去。
 * 设了「每人多长时间内只回一次」时，默认话术、每条关键词话术对同一个人在这段时间内各只回一次：发之前先到服务端占位，占不到就不发。
 */

// 重连后 Telegram 会把断线期间积压的更新一次性补推过来，太旧的消息不再回，避免一上线刷屏
const MAX_MESSAGE_AGE = 30 * 60;
// 同一个人 15 分钟内最多自动回 3 次
const REPLY_LIMIT_WINDOW = 15 * 60;
const REPLY_LIMIT_COUNT = 3;
// 自动混淆插入的符号，只插在汉字后面
const OBFUSCATION_CHARS = ['⁺', 'ﾞ', 'ﾟ'];
const KEYWORD_PLACEHOLDER = /\$关键词\$/g;

// 一条要发的消息：文字、名片或核对清单（后两种只有默认话术里有）
type ReplyPart = { type: 'text'; text: string }
  | { type: 'card'; phone: string; name: string }
  | { type: 'todo'; title: string; items: Array<{ id: number; title: string }>; othersCanComplete: boolean };

type PendingReply = {
  // 按顺序分开发的多条消息（至少一条）
  parts: ReplyPart[];
  // 每人多长时间内只回一次时的话术标识：default 或 rule:关键词；不限制时为空
  onceKey?: string;
  onceMinutes?: number;
};

type PendingSend = {
  // 发送失败时 Telegram 回推的错误，由 sendPart 取走
  error?: string;
};

type ReplyState = {
  firstReplyAt: number;
  count: number;
  lastText: string;
};

const pendingReplies = new Map<string, number>();
const replyStates = new Map<string, ReplyState>();
// 已发出、还没确认结果的自动回复：键是会话+本地消息 id，失败更新回来时把错误记在上面
const pendingSends = new Map<string, PendingSend>();
// 服务端查不到结果时，先隔这么久再去问
const ONCE_RETRY_DELAY = 60 * 1000;
// 本页已经确认时间内回过的（会话|话术标识 → 下次能回的毫秒时间），省得每条消息都去服务端问一次
const onceUntil = new Map<string, number>();
// 多条话术之间隔 1-3 秒随机发
const PART_GAP_MIN = 1000;
const PART_GAP_MAX = 3000;
// 每个会话的轮次：对方又发了消息、或人工回了就加一，正在分条发的那一轮剩下的就不发了
const chatRounds = new Map<string, number>();
// 正在分条发的会话：自己发出去的消息也会回推过来，不能当成人工回复
const sendingChats = new Set<string>();

function bumpRound(chatId: string) {
  chatRounds.set(chatId, (chatRounds.get(chatId) || 0) + 1);
}

addActionHandler('apiUpdate', (global, actions, update): ActionReturnType => {
  if (update['@type'] === 'updateMessageSendFailed') {
    handleSendFailed(update.chatId, update.localId, update.error);
    return;
  }

  if (update['@type'] !== 'updateMessage' || !update.isFromNew) return;

  handleIncomingMessage(update.message);
});

function handleIncomingMessage(message: Partial<ApiMessage>) {
  const { chatId } = message;
  if (!chatId) return;

  // 人工在这个会话回了消息，就不再自动接管
  if (message.isOutgoing) {
    if (!sendingChats.has(chatId)) {
      cancelPendingReply(chatId);
      bumpRound(chatId);
    }
    return;
  }

  const config = getSeatAutoReplyConfig();
  if (!config?.enabled) return;
  // 只回私聊，且对方是普通用户：机器人、官方通知号都跳过
  if (!isUserId(chatId) || chatId === SERVICE_NOTIFICATIONS_USER_ID) return;

  const user = selectUser(getGlobal(), chatId);
  if (!user || user.isSelf || isUserBot(user)) return;
  if (!(user.isContact ? config.contacts : config.strangers)) return;

  const text = message.content?.text?.text;
  if (!text) return;
  if (message.date && getServerTime() - message.date > MAX_MESSAGE_AGE) return;
  if (isReplyLimitReached(chatId, text)) return;

  const reply = buildReplyText(config, text);
  if (!reply) return;

  scheduleReply(chatId, reply, config.delay, text);
}

/** 同一个人 15 分钟窗口内已自动回过 3 次，且这次来的内容跟上次一模一样，就不再回 */
function isReplyLimitReached(chatId: string, incoming: string) {
  const state = replyStates.get(chatId);
  if (!state) return false;

  const now = getServerTime();
  if (now - state.firstReplyAt >= REPLY_LIMIT_WINDOW) {
    replyStates.delete(chatId);
    return false;
  }

  return state.count >= REPLY_LIMIT_COUNT && state.lastText === incoming;
}

function countReply(chatId: string, incoming: string) {
  const now = getServerTime();
  const state = replyStates.get(chatId);
  if (!state || now - state.firstReplyAt >= REPLY_LIMIT_WINDOW) {
    replyStates.set(chatId, { firstReplyAt: now, count: 1, lastText: incoming });
    return;
  }

  state.count += 1;
  state.lastText = incoming;
}

/** 关键词按规则顺序匹配，命中第一条就用它的话术，都不命中用默认话术 */
function buildReplyText(config: AutoReplyConfig, incoming: string): PendingReply | undefined {
  let parts: ReplyPart[] = [];
  let matched = '';
  let ruleKey = '';

  for (const rule of config.rules) {
    for (const keyword of rule.keywords) {
      if (keyword && incoming.includes(keyword)) {
        matched = keyword;
        parts = pickParts(rule.scripts, rule.script)
          .map((text) => ({ type: 'text', text: text.replace(KEYWORD_PLACEHOLDER, keyword) }));
        ruleKey = `rule:${rule.keywords.join(',')}`;
        break;
      }
    }
    if (matched) break;
  }

  if (!parts.length) {
    parts = pickDefaultParts(config);
    ruleKey = 'default';
  }
  if (!parts.length) return undefined;

  const onceMinutes = Math.max(0, Number(config.onceMinutes) || 0);
  return {
    // 混淆只对文字做，名片、核对清单原样发
    parts: config.obfuscation > 0
      ? parts.map((part) => (part.type === 'text' ? { ...part, text: obfuscate(part.text, config.obfuscation) } : part))
      : parts,
    onceKey: onceMinutes > 0 ? ruleKey : undefined,
    onceMinutes,
  };
}

/** 多条话术去空；老配置只有单条 */
// 默认话术：有带类型的 parts 就用它，老配置只有文字 texts / text
function pickDefaultParts(config: AutoReplyConfig): ReplyPart[] {
  const list: ReplyPart[] = [];
  for (const part of config.parts || []) {
    const normalized = normalizeDefaultPart(part);
    if (normalized) list.push(normalized);
  }
  if (list.length) return list;
  return pickParts(config.texts, config.text).map((text) => ({ type: 'text', text }));
}

function normalizeDefaultPart(part: AutoReplyPart): ReplyPart | undefined {
  if (part.type === 'card') {
    const phone = String(part.phone || '').trim();
    return phone ? { type: 'card', phone, name: String(part.name || '').trim() } : undefined;
  }
  if (part.type === 'todo') {
    const title = String(part.title || '').trim();
    const items = (part.items || [])
      .map((item) => String(item?.title || '').trim())
      .filter(Boolean)
      .map((itemTitle, index) => ({ id: index + 1, title: itemTitle }));
    return title && items.length
      ? { type: 'todo', title, items, othersCanComplete: Boolean(part.others_can_complete) }
      : undefined;
  }
  const text = String(part.text || '').trim();
  return text ? { type: 'text', text } : undefined;
}

function pickParts(parts: string[] | undefined, single: string) {
  const list = (parts || []).map((part) => String(part || '').trim()).filter(Boolean);
  if (list.length) return list;
  const one = String(single || '').trim();
  return one ? [one] : [];
}

/**
 * 自动混淆：按比例随机挑一部分汉字，在它们后面插一个符号字符，
 * 让每次发出去的文本都不一样，降低被风控按重复内容识别的概率
 */
function obfuscate(text: string, ratio: number) {
  const positions: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const code = text.codePointAt(i);
    if (!code || code > 0xFFFF) {
      i++;
      continue;
    }
    if (code >= 0x4E00 && code <= 0x9FFF) positions.push(i);
  }

  const insertCount = Math.floor(positions.length * ratio);
  if (insertCount <= 0) return text;

  shuffle(positions);
  positions.splice(insertCount, positions.length);
  positions.sort((a, b) => a - b);

  let next = 0;
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += text[i];
    if (next < positions.length && i === positions[next]) {
      result += OBFUSCATION_CHARS[Math.floor(Math.random() * OBFUSCATION_CHARS.length)];
      next++;
    }
  }

  return result;
}

function shuffle(list: number[]) {
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
}

/**
 * 延时接管：对方又发来消息就重新计时（等他说完），人工先回了就整个取消，
 * 延时为 0 时下一个事件循环即发出
 */
function scheduleReply(chatId: string, reply: PendingReply, delay: number, incoming: string) {
  // 这句时间内已经回过了：不发，也不打断前面还在等的另一句
  if (reply.onceKey && isOnceBlocked(`${chatId}|${reply.onceKey}`)) return;
  cancelPendingReply(chatId);
  bumpRound(chatId);

  const timer = window.setTimeout(() => {
    pendingReplies.delete(chatId);
    void deliverReply(chatId, reply, incoming);
  }, Math.max(0, delay) * 1000);

  pendingReplies.set(chatId, timer);
}

function cancelPendingReply(chatId: string) {
  const timer = pendingReplies.get(chatId);
  if (timer === undefined) return;

  window.clearTimeout(timer);
  pendingReplies.delete(chatId);
}

/**
 * 走客户端自己的发送流程：先往会话里插一条本地消息（界面立刻能看到这句回复），再真正发出去，
 * 发送结果由 Telegram 的回执替换成正式消息。直接调 MTProto 发的消息不会进本地会话，
 * 要刷新才看得到
 */
async function deliverReply(chatId: string, reply: PendingReply, incoming: string) {
  const { parts, onceKey } = reply;
  if (onceKey) {
    const doneKey = `${chatId}|${onceKey}`;
    if (isOnceBlocked(doneKey)) return;
    // 先在本页占住再去服务端占位，同一时刻两次到点也只会发一次
    onceUntil.set(doneKey, Date.now() + (reply.onceMinutes || 0) * 60 * 1000);
    const claim = await claimSeatAutoReply(chatId, onceKey);
    if (!claim.claimed) {
      onceUntil.set(doneKey, claim.until > 0 ? claim.until * 1000 : Date.now() + ONCE_RETRY_DELAY);
      return;
    }
  }

  const global = getGlobal();
  const user = selectUser(global, chatId);
  const name = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || chatId : chatId;

  const round = chatRounds.get(chatId) || 0;
  let sentCount = 0;
  sendingChats.add(chatId);
  try {
    for (let i = 0; i < parts.length; i++) {
      if (i > 0) {
        await pause(PART_GAP_MIN + Math.floor(Math.random() * (PART_GAP_MAX - PART_GAP_MIN)));
        // 对方又发了消息或人工回了：剩下的不发（已经发出去的照样算回过）
        if ((chatRounds.get(chatId) || 0) !== round) break;
      }
      const error = await sendPart(chatId, parts[i]);
      if (!error) {
        sentCount++;
        continue;
      }
      const isSkipped = isSkippablePartError(error);
      const result = isSkipped ? '发不了，已跳过' : '发送失败，后面的不再发';
      reportSeatAutoReply(name, `第 ${i + 1} 条${PART_LABELS[parts[i].type]}${result}：${error}`);
      if (!isSkipped) break;
    }
  } finally {
    sendingChats.delete(chatId);
  }

  // 一条都没发出去：这次当没回过，占位放掉
  if (!sentCount) {
    releaseOnce(chatId, onceKey);
    return;
  }

  countReply(chatId, incoming);
  // 自动回过的会话标成未读，人工回来还能看到它在等人回
  void callApi('markSeatChatUnread', { chatId });
  reportSeatAutoReply(name);
}

/** 发一条：成功返回 undefined，失败返回错误原因 */
async function sendPart(chatId: string, part: ReplyPart): Promise<string | undefined> {
  if (part.type !== 'text') return sendMediaPart(chatId, part);

  const { text } = part;
  const global = getGlobal();
  const chat = selectChat(global, chatId);
  if (!chat) return '本地没有这个会话';

  const params = { chat, text, lastMessageId: selectChatLastMessageId(global, chatId) };
  const localMessage = await callApi('sendMessageLocal', params);
  if (!localMessage) return '本地消息创建失败';

  const sendKey = getSendKey(chatId, localMessage.id);
  const pending: PendingSend = {};
  pendingSends.set(sendKey, pending);

  await callApi('sendMessage', { ...params, localMessage });

  // 失败时 handleSendFailed 已经把错误记在 pending 上
  pendingSends.delete(sendKey);
  return pending.error;
}

// 名片、核对清单直接发到这个私聊；核对清单要求席位号开通 Telegram Premium
async function sendMediaPart(chatId: string, part: Exclude<ReplyPart, { type: 'text' }>) {
  try {
    const result = part.type === 'card'
      ? await callApi('sendSeatChatContactCard', { chatId, phone: part.phone, firstName: part.name || part.phone })
      : await callApi('sendSeatChatTodo', {
        chatId,
        todo: { title: part.title, items: part.items, othersCanComplete: part.othersCanComplete },
      });
    return result ? undefined : '本地没有这个会话';
  } catch (err) {
    return err instanceof Error ? err.message : String(err);
  }
}

const PART_LABELS: Record<ReplyPart['type'], string> = { text: '文字', card: '名片', todo: '核对清单' };

/**
 * 只是这一条自己发不了（核对清单要会员、名片号码不对、内容不合规）才跳过，后面的照发；
 * 限流、冻结、申诉限制、登录失效、对方拉黑或隐私不收、网络问题等其它错误都停下，后面的不再发。
 * 与 网页代发服务器node接管/src/autoReply.ts 同一口径
 */
function isSkippablePartError(error: string) {
  const upper = error.toUpperCase();
  // 对方设了只收会员私聊：换哪条都发不过去
  if (upper.includes('PRIVACY_PREMIUM_REQUIRED')) return false;

  return (upper.includes('PREMIUM') && upper.includes('REQUIRED'))
    || upper.includes('TODO_')
    || upper.includes('MEDIA_INVALID')
    || upper.includes('MEDIA_EMPTY')
    || upper.includes('PHONE_NUMBER_INVALID')
    || upper.includes('CHAT_SEND_MEDIA_FORBIDDEN')
    || upper.includes('MESSAGE_TOO_LONG')
    || upper.includes('MESSAGE_EMPTY');
}

function pause(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function handleSendFailed(chatId: string, localId: number, error: string) {
  const sendKey = getSendKey(chatId, localId);
  const pending = pendingSends.get(sendKey);
  if (pending === undefined) return;

  pending.error = error || '发送失败';
}

function isOnceBlocked(doneKey: string) {
  const until = onceUntil.get(doneKey);
  if (until === undefined) return false;
  if (until > Date.now()) return true;
  onceUntil.delete(doneKey);
  return false;
}

/** 占了位却没发出去：本页和服务端都放掉，下次还能回 */
function releaseOnce(chatId: string, onceKey?: string) {
  if (!onceKey) return;
  onceUntil.delete(`${chatId}|${onceKey}`);
  releaseSeatAutoReply(chatId, onceKey);
}

function getSendKey(chatId: string, localId: number) {
  return `${chatId}:${localId}`;
}
